
import { Options } from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import { Field, Form } from "vee-validate";
import HelpIcon from "../../atoms/HelpIcon.vue";
import FieldLabel from "../../atoms/FieldLabel.vue";
import * as yup from "yup";
import Store from "../../../store";
import FieldError from "../../atoms/FieldError.vue";
import FormError from "../../atoms/FormError.vue";
import ActionButton from "@/components/atoms/ActionButton.vue";
import { CreateAsBroadcastRequest, AsBroadcastTypeEnum, AsApi, GetAsBroadcastResponse, PricePlanEnum } from "../../../autogen";
import UserSelector from "../../molecules/UserSelector.vue";
import Datepicker from "vue3-datepicker";
import { ja } from "date-fns/locale";
import FormMixin from "../../../helpers/mixins/form";
import Toggle from "@vueform/toggle";
import { InstructionLevel } from "@/types";
import UIkit from "uikit";

type FormValues = CreateAsBroadcastRequest & {
  startTime: string;
  endTime: string;
  startDate?: Date;
  endDate?: Date;
};

@Options({
  components: {
    Field,
    Form,
    HelpIcon,
    FieldLabel,
    FieldError,
    FormError,
    ActionButton,
    Datepicker,
    UserSelector,
    Toggle
  }
})
export default class ASGroupForm extends FormMixin {
  private readonly auth = Store.getInstance().getAuth()!;
  globalErrors: { message: string }[] = [];
  @Prop({ type: Object }) broadcastGroup?: GetAsBroadcastResponse;
  formName = "アセスメントサーベイグループ";
  formValues: FormValues = {
    name: "",
    type: AsBroadcastTypeEnum.Employee,
    startDate: new Date(),
    startDateTime: "",
    endDateTime: "",
    completionEmail: "",
    startTime: "0",
    endTime: "23",
    adminRemind: false,
    participantRemind: false
  };
  locale = ja;
  validationSchema!: yup.AnyObjectSchema;
  bcType = AsBroadcastTypeEnum;
  isApplicantSelectable = false;
  beforeCreate() {
    this.validationSchema = yup.object({
      name: yup
        .string()
        .trim()
        .required(this.$t("input_required", { field: "実施内容名" })),
      type: yup.string().oneOf(Object.values(AsBroadcastTypeEnum)),
      startDate: yup.string().required(this.$t("input_required", { field: "受検開始日" })),
      endDate: yup.string().required(this.$t("input_required", { field: "受検終了日" })),
      startTime: yup.string().required(this.$t("input_required", { field: "受検開始時刻" })),
      endTime: yup.string().required(this.$t("input_required", { field: "受検終了時刻" })),
      completionEmail: yup.string().email(this.$t("input_email"))
    });
  }
  currentType: AsBroadcastTypeEnum = AsBroadcastTypeEnum.Employee;
  beforeMount() {
    if (this.broadcastGroup) {
      const startDateTime = new Date(this.broadcastGroup.startDateTime);
      const endDateTime = new Date(this.broadcastGroup.endDateTime);
      this.formValues = {
        name: this.broadcastGroup.name,
        type: this.broadcastGroup.type,
        startDateTime: this.broadcastGroup.startDateTime,
        endDateTime: this.broadcastGroup.endDateTime,
        completionEmail: this.broadcastGroup.completionEmail,
        startTime: String(startDateTime.getHours()),
        endTime: String(endDateTime.getHours()),
        startDate: startDateTime,
        endDate: endDateTime,
        adminRemind: this.broadcastGroup.adminRemind,
        participantRemind: this.broadcastGroup.participantRemind
      };
    }
    this.getPlan();
  }
  async onBeforeSubmit(values: FormValues) {
    const startDateTime = new Date(
      values.startDate!.getFullYear(),
      values.startDate!.getMonth(),
      values.startDate!.getDate(),
      values.startDate!.getHours()
    );
    startDateTime.setHours(parseInt(values.startTime));
    const endDateTime = new Date(
      values.endDate!.getFullYear(),
      values.endDate!.getMonth(),
      values.endDate!.getDate(),
      values.endDate!.getHours()
    );
    endDateTime.setHours(parseInt(values.endTime));
    await this.onSubmit(async () => {
      if (this.broadcastGroup) {
        await new AsApi().updateAsBroadcast({
          asBroadcastId: this.broadcastGroup.asBroadcastId,
          updateAsBroadcastRequest: {
            type: values.type,
            name: values.name,
            startDateTime: startDateTime.toISOString(),
            endDateTime: endDateTime.toISOString(),
            completionEmail: values.completionEmail,
            adminRemind: values.adminRemind,
            participantRemind: values.participantRemind
          }
        });
      } else {
        await new AsApi().createAsBroadcast({
          createAsBroadcastRequest: {
            type: values.type,
            name: values.name,
            startDateTime: startDateTime.toISOString(),
            endDateTime: endDateTime.toISOString(),
            completionEmail: values.completionEmail,
            adminRemind: values.adminRemind,
            participantRemind: values.participantRemind
          }
        });
      }
      this.$emit("submitted");
    });
  }
  onTypeSelectionChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.currentType = target.value as AsBroadcastTypeEnum;
  }
  async onDeleteButtonClick() {
    this.onDeleteAsg(async () => {
      await new AsApi().deleteAsBroadcast({
        asBroadcastId: this.broadcastGroup!.asBroadcastId
      });
    }, "アセスメントサーベイを削除します。よろしいでしょうか？\n※実施期間中のアセスメントサーベイを削除するには受験者を全員削除してください");
  }
  async onDeleteAsg(fn: () => Promise<void>, message?: string | string[]) {
    this.storeInstance.setInstructionContent(
      InstructionLevel.Confirm,
      message || `${this.formName}を削除します。よろしいでしょうか？`,
      async () => {
        try {
          await fn();
          this.storeInstance.setInstructionContent(InstructionLevel.Info, `${this.formName}の削除が完了しました`);
          this.$emit("deleted");
        } catch (err) {
          console.error(err);
          this.handleSubmissionError(err);
        }
      }
    );
  }
  @Watch("auth.pricePlan")
  getPlan() {
    const planCode = this.auth.pricePlan;
    this.isApplicantSelectable = planCode !== PricePlanEnum.Trial;
  }
  setEndDate(w: number) {
    if (this.formValues.startDate) {
      var dt = new Date(this.formValues.startDate);
      dt.setDate(dt.getDate() + 7 * w);
      this.formValues.endDate = dt;
    }
  }
}
